/**
 * Capa 2: Esquemas de Validación Zod — Onboarding de Clientes
 * Espejo riguroso de backend/app/modulos/onboarding_clientes/schemas/v1/onboarding_clientes_schemas.py
 */

import { z } from 'zod';

// ─── Paso 1: Información de Granja / Predio ───────────────────────────────────

export const paso1GranjaSchema = z.object({
  nombre_granja: z
    .string()
    .trim()
    .min(3, { message: 'El nombre de la granja debe tener al menos 3 caracteres' })
    .max(150, { message: 'El nombre de la granja no puede superar los 150 caracteres' }),
  pais: z
    .string()
    .trim()
    .min(2, { message: 'Selecciona un país' })
    .max(50, { message: 'País no válido' })
    .default('Colombia'),
  departamento_estado: z
    .string()
    .trim()
    .min(2, { message: 'Selecciona un departamento o estado' })
    .max(100, { message: 'Departamento/Estado demasiado largo' }),
  municipio_ciudad: z
    .string()
    .trim()
    .min(2, { message: 'Selecciona un municipio o ciudad' })
    .max(100, { message: 'Municipio/Ciudad demasiado largo' }),
  idioma: z
    .string()
    .trim()
    .min(2, { message: 'Código de idioma inválido' })
    .max(5)
    .default('es'),
  capacidad_total_aves: z
    .number()
    .int({ message: 'La capacidad debe ser un número entero' })
    .nonnegative({ message: 'La capacidad no puede ser negativa' })
    .nullable()
    .optional(),
  sistema_alojamiento: z
    .string()
    .trim()
    .max(50, { message: 'Máximo 50 caracteres' })
    .nullable()
    .optional(),
});

export type Paso1GranjaInput = z.input<typeof paso1GranjaSchema>;
export type Paso1GranjaOutput = z.output<typeof paso1GranjaSchema>;
export type Paso1GranjaSchemaType = Paso1GranjaOutput;

// ─── Paso 2: Información del Usuario Titular ──────────────────────────────────

export const paso2UsuarioSchema = z.object({
  primer_nombre: z
    .string()
    .trim()
    .min(2, { message: 'El primer nombre debe tener al menos 2 caracteres' })
    .max(50, { message: 'Máximo 50 caracteres' }),
  segundo_nombre: z
    .string()
    .trim()
    .max(50, { message: 'Máximo 50 caracteres' })
    .optional()
    .or(z.literal('')),
  primer_apellido: z
    .string()
    .trim()
    .min(2, { message: 'El primer apellido debe tener al menos 2 caracteres' })
    .max(50, { message: 'Máximo 50 caracteres' }),
  segundo_apellido: z
    .string()
    .trim()
    .max(50, { message: 'Máximo 50 caracteres' })
    .optional()
    .or(z.literal('')),
  whatsapp: z
    .string()
    .trim()
    .max(30, { message: 'Número demasiado largo' })
    .optional()
    .or(z.literal('')),
  codigo_pais_whatsapp: z.string().optional().default('+57'),
  email: z
    .string()
    .trim()
    .email({ message: 'Ingresa un correo electrónico válido' })
    .transform((val) => val.toLowerCase())
    .optional()
    .or(z.literal('')),
});

export const step2UsuarioFormSchema = paso2UsuarioSchema.extend({
  whatsapp: z
    .string()
    .trim()
    .min(7, { message: 'Ingresa un número de WhatsApp válido' })
    .max(30, { message: 'Número demasiado largo' }),
  email: z
    .string()
    .trim()
    .min(5, { message: 'El correo electrónico es requerido' })
    .email({ message: 'Ingresa un correo electrónico válido (ej. usuario@granja.com)' })
    .transform((val) => val.toLowerCase()),
});

export type Paso2UsuarioInput = z.input<typeof paso2UsuarioSchema>;
export type Paso2UsuarioOutput = z.output<typeof paso2UsuarioSchema>;
export type Paso2UsuarioSchemaType = Paso2UsuarioOutput;

export type Step2UsuarioFormInput = z.input<typeof step2UsuarioFormSchema>;
export type Step2UsuarioFormOutput = z.output<typeof step2UsuarioFormSchema>;

// ─── Paso 3: Credenciales de Acceso y Aceptación Legal ────────────────────────

export const paso3CredencialesSchema = z
  .object({
    email: z
      .string()
      .trim()
      .min(5, { message: 'El correo electrónico es requerido' })
      .email({ message: 'Ingresa un correo electrónico válido (ej. usuario@granja.com)' })
      .transform((val) => val.toLowerCase()),
    telefono_movil: z
      .string()
      .trim()
      .max(30, { message: 'Número de teléfono demasiado largo' })
      .optional()
      .or(z.literal('')),
    password: z
      .string()
      .min(8, { message: 'La contraseña debe tener mínimo 8 caracteres' })
      .refine((val) => /[a-zA-Z]/.test(val), {
        message: 'La contraseña debe contener al menos una letra',
      })
      .refine((val) => /[0-9]/.test(val), {
        message: 'La contraseña debe contener al menos un número',
      }),
    confirm_password: z
      .string()
      .min(1, { message: 'Por favor confirma tu contraseña' }),
    acepta_terminos_y_condiciones: z.boolean().refine((val) => val === true, {
      message: 'Debes aceptar los Términos y Condiciones y Política de Privacidad para continuar',
    }),
  })
  .refine((data) => data.password === data.confirm_password, {
    message: 'Las contraseñas no coinciden',
    path: ['confirm_password'],
  });

export type Paso3CredencialesSchemaType = z.infer<typeof paso3CredencialesSchema>;

// ─── Esquema Completo para Validación Transaccional ───────────────────────────

export const onboardingCompletoSchema = z.object({
  paso1: paso1GranjaSchema,
  paso2: paso2UsuarioSchema,
  paso3: paso3CredencialesSchema,
});

export type OnboardingCompletoSchemaType = z.infer<typeof onboardingCompletoSchema>;
